import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Plus, Languages, Package2, Check, LogIn, User as UserIcon, LogOut, LayoutDashboard, Search, Shield, Bookmark, Target } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { MatchBadge } from "./MatchBadge";

export const Header = () => {
  const { t, lang, setLang, languages } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const onBoard = location.pathname === "/";
  const onProducts = location.pathname === "/products";
  const currentLang = languages.find((l) => l.code === lang) || languages[0];
  const [search, setSearch] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/products?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="glass-nav sticky top-0 z-40 w-full">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 md:px-10 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-y-2">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group shrink-0">
          <div className="flex items-center justify-center h-9 w-9 rounded-md bg-foreground text-background group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
            <Package2 className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-base sm:text-lg font-bold tracking-tight">{t("brand")}</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium mt-0.5">
              buy · request · board
            </span>
          </div>
        </Link>

        <form
          onSubmit={submitSearch}
          className="flex flex-1 max-w-md mx-2 sm:mx-4 md:mx-6 order-last md:order-none w-full md:w-auto"
          data-testid="header-search-form"
        >
          <div className="relative w-full">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              data-testid="header-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className="w-full h-9 pl-8 pr-3 rounded-full border border-border bg-white text-sm placeholder:text-muted-foreground focus:outline-none focus:border-foreground/40 focus:ring-2 focus:ring-primary/20 transition"
            />
          </div>
        </form>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-white h-9 px-3 hover:border-foreground/30 transition-colors"
              >
                <Languages className="h-4 w-4" strokeWidth={2} />
                <span className="text-xs font-semibold tracking-wide font-mono-brand">{currentLang.flag}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white w-56 max-h-[70vh] overflow-y-auto" data-testid="lang-menu">
              <DropdownMenuLabel className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                {t("language")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono-brand text-[10px] font-bold text-muted-foreground w-5">{l.flag}</span>
                    <span className="text-sm">{l.label}</span>
                  </div>
                  {lang === l.code && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && (
            <button
              data-testid="header-matches-link"
              onClick={() => navigate("/matches")}
              aria-label="Best match feed"
              className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-white hover:border-foreground/30 transition-colors"
            >
              <Target className="h-4 w-4" strokeWidth={2} />
              <span className="absolute -top-1 -right-1">
                <MatchBadge testId="header-match-badge" />
              </span>
            </button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="relative inline-flex items-center gap-2 rounded-full bg-foreground text-background h-9 px-3 hover:bg-primary transition-colors"
                >
                  <div className="h-6 w-6 rounded-full bg-background/20 flex items-center justify-center text-xs font-bold">
                    {user.display_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <span className="text-xs font-semibold max-w-[80px] truncate hidden sm:inline">
                    {user.display_name}
                  </span>
                  <span className="absolute -top-1 -right-1">
                    <MatchBadge testId="user-menu-match-badge" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-56">
                <DropdownMenuLabel className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">
                  Signed in as
                </DropdownMenuLabel>
                <DropdownMenuItem className="text-sm truncate opacity-70 pointer-events-none">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-my-listings" onClick={() => navigate("/my-listings")}>
                  <LayoutDashboard className="h-4 w-4 mr-2" /> My listings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-saved" onClick={() => navigate("/saved")}>
                  <Bookmark className="h-4 w-4 mr-2" /> Saved
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-matches" onClick={() => navigate("/matches")}>
                  <Target className="h-4 w-4 mr-2" /> Best match feed
                  <span className="ml-auto"><MatchBadge testId="menu-match-badge" /></span>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/s/${encodeURIComponent(user.display_name)}`)}>
                  <UserIcon className="h-4 w-4 mr-2" /> My profile
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield className="h-4 w-4 mr-2 text-destructive" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="header-signin-btn"
              onClick={() => navigate("/login")}
              variant="outline"
              className="rounded-md gap-1.5 h-9"
            >
              <LogIn className="h-4 w-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">Sign in</span>
            </Button>
          )}

          {onBoard && (
            <Button
              data-testid="header-post-request-btn"
              onClick={() => navigate(user ? "/new" : "/login")}
              className="rounded-md gap-1.5 h-9"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t("nav_post")}</span>
              <span className="sm:hidden">New</span>
            </Button>
          )}
          {onProducts && (
            <Button
              data-testid="header-list-product-btn"
              onClick={() => navigate(user ? "/new-product" : "/login")}
              className="rounded-md gap-1.5 h-9"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t("nav_post_product")}</span>
              <span className="sm:hidden">New</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
